import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpHeaderResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthenticationService, Context } from '@app/core/authentication/authentication.service';
import { JwtHelperService } from '@auth0/angular-jwt';

import { HttpStatusCode } from '@app/constants/error';
import { Logger } from '../logger.service';

const log = new Logger('ErrorHandlerInterceptor');
@Injectable({
  providedIn: 'root'
})

export class ErrorHandlerInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private readonly auth: AuthenticationService, private readonly jwtHelper: JwtHelperService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const token = sessionStorage.getItem('token');

    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMsg = error.status ? `${error.status} - ${error.statusText}` : 'Server error';
          if (error.status === HttpStatusCode.UNAUTHORIZED) {
            if (this.jwtHelper.isTokenExpired(token) === true) {
              return this.handle401Error(request, next);
            }
            else {
              log.error(`Unauthorized Access: ${errorMsg}`);
              this.auth.logout();
            }
          }
          else if (error.status === HttpStatusCode.FORBIDDEN) {
            log.error(`Forbidden Access: ${errorMsg}`);
            this.auth.logout();
          }
          else if (error.status < 200 || error.status >= 300) {
            log.error(`Request has failed ${errorMsg}`);
          }
          else {
            log.error(error);
          }
          return throwError(error);
        })
      );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({ headers: request.headers.set("Authorization", `Bearer ${token}`) });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.auth.RefreshAuthToken().pipe(
        switchMap((token: any) => {
          if (token) {
            this.isRefreshing = false;
            sessionStorage.setItem('token', token);
            const context: Context = { idms: this.auth.ParseJwt(token), rbac: this.auth.context.rbac };
            this.auth.setContext(context);
            this.refreshTokenSubject.next(token);
            return next.handle(this.addTokenHeader(request, token));
          }
          else {
            this.auth.logout();
          }
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.auth.logout();
          return throwError(err);
        })
      );
    }
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }
}
